import { Button, Icon, Shadow, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useMemo } from 'react';
import { useSetRecoilState } from 'recoil';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { selectedTransactionFlowProduct } from '../../../../store/transactionFlow/atom';
import { currentPositionAggregatedQuery } from '../../../../store/transactionFlow/selector';
import {
  getRenderFields,
  transactionFlowProducts,
  TransactionFlowProductsType,
} from '../../constants';

import { CardLoadingItem } from './CardLoadingItem';
import { CardProductItem } from './CardProductItem';

const loadingCards = Array.from({ length: 7 }).map((_, index) => <CardLoadingItem key={index} />);

export const CurrentPosition: React.FC = () => {
  const setSelectedProduct = useSetRecoilState(selectedTransactionFlowProduct);
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const {
    data: PositionProducts,
    loading,
    refresher,
  } = useRecoilQuery(currentPositionAggregatedQuery);

  const handleDetailsNavigate = useCallback(
    (product: TransactionFlowProductsType) => {
      if (!PositionProducts) {
        return;
      }

      setSelectedProduct((prev) => ({
        ...prev,
        [product.key]: { product, data: PositionProducts[product.key] },
      }));

      navigate('TransactionFlowDetails', { product: product.key });
    },
    [PositionProducts, navigate, setSelectedProduct]
  );

  const productList = useMemo(() => {
    if (!PositionProducts) {
      return [];
    }

    return transactionFlowProducts.map((product, index) => (
      <CardProductItem
        hasButtonDetails
        key={index}
        label={product.label}
        color={product.color}
        productDefinition={product}
        productData={PositionProducts[product.key]}
        renderFields={getRenderFields(product.key, PositionProducts[product.key])}
        onPressDetails={() => handleDetailsNavigate(product)}
      />
    ));
  }, [PositionProducts, handleDetailsNavigate]);

  return (
    <>
      <Shadow level={1} borderRadius={BoxRadius.sm} padding={Spacing['inset.xs']}>
        <VStack
          space={{ base: Spacing['stack.xxxs'], lg: Spacing['stack.xxs'] }}
          pb={Spacing['stack.xl']}>
          <HStack justifyContent="space-between" alignItems="center">
            <Text fontSize={Typography['fontsize.md']} variant="semibold">
              Dados referentes: {Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy')} (atual d0)
            </Text>
            <VStack space={Spacing['stack.quarck']} alignItems="flex-end">
              <Button
                variant="textLink"
                isLoading={loading}
                padding={0}
                rightIcon={
                  <Icon
                    name="Refresh"
                    size="xxs"
                    testID="update-button-icon"
                    color={Color['brand.tertiary.principal']}
                  />
                }
                onPress={refresher}>
                Atualizar
              </Button>
            </VStack>
          </HStack>

          <VStack space={Spacing['stack.nano']}>{loading ? loadingCards : productList}</VStack>
        </VStack>
      </Shadow>
    </>
  );
};
